import React, { useEffect, useMemo, useRef, useState } from "react";
import { App, FloatButton } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import useCategories from "./hooks/useCategories";
import MobileCategories from "@/components/MobileCategories.jsx";
import { filterData } from "./utils/filterData";
import { buildDataSource } from "./utils/buildDataSource";
import { useUI } from '@/context/UIContext'

export default function CategoryTable() {
  const { message } = App.useApp();
  const { data = [], loading, reload, createRoot, createChild, updateCategoryName, removeCategory } = useCategories();

  const { searchQuery,setHeaderTitle } = useUI(); // ← общий поиск

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [showRootDraft, setShowRootDraft] = useState(false);
  const [rootName, setRootName] = useState("");
  const [childDrafts, setChildDrafts] = useState({});

  const LS_KEY = "materialCategories.expanded";
  const expandedHydratedRef = useRef(false);


      useEffect(() => {
    setHeaderTitle("Категорії матеріалів");
    return () => setHeaderTitle("WHFOF"); // восстановить при уходе
  }, [setHeaderTitle]);

  useEffect(() => {
    if (expandedHydratedRef.current) return;
    const raw = typeof window !== "undefined" ? localStorage.getItem(LS_KEY) : null;
    if (raw) {
      try {
        const fromLS = JSON.parse(raw);
        if (Array.isArray(fromLS)) {
          setExpandedRowKeys(fromLS);
          expandedHydratedRef.current = true;
          return;
        }
      } catch {}
    }
    if (Array.isArray(data) && data.length) {
      const roots = data.map((d) => d._id);
      setExpandedRowKeys((prev) => Array.from(new Set([...prev, ...roots])));
      expandedHydratedRef.current = true;
    }
  }, [data]);

  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) return;
    const allIds = new Set(data.flatMap((r) => [r._id, ...(r.children || []).map((c) => c._id)]));
    setExpandedRowKeys((prev) => prev.filter((k) => allIds.has(k)));
  }, [data]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try { localStorage.setItem(LS_KEY, JSON.stringify(expandedRowKeys)); } catch {}
  }, [expandedRowKeys]);

  const filtered = useMemo(() => filterData(data, searchQuery), [data, searchQuery]);

  const dataSource = useMemo(
    () => buildDataSource({ base: filtered, showRootDraft, childDrafts }),
    [filtered, showRootDraft, childDrafts]
  );

  const saveRoot = async () => {
    const name = rootName.trim();
    if (name.length < 2) { message.warning("Назва має містити щонайменше 2 символи"); return; }
    await createRoot(name);
    setRootName(""); setShowRootDraft(false); await reload();
  };

  const saveChild = async (parentId) => {
    const name = (childDrafts[parentId] || "").trim();
    if (name.length < 2) { message.warning("Назва має містити щонайменше 2 символи"); return; }
    await createChild(parentId, name);
    setChildDrafts((m) => { const { [parentId]: _, ...rest } = m; return rest; });
    setExpandedRowKeys((ks) => Array.from(new Set([...ks, parentId])));
    await reload();
  };

  const renameCategory = async (id, nextName) => {
    const name = (nextName || "").trim();
    if (name.length < 2) { message.warning("Назва має містити щонайменше 2 символи"); return false; }
    await updateCategoryName(id, name); await reload(); message.success("Назву оновлено"); return true;
  };

  const handleDelete = async (id) => { await removeCategory(id); await reload(); message.success("Категорію видалено"); };

  return (
    <>
      <MobileCategories
        loading={loading}
        dataSource={dataSource}
        rootName={rootName}
        setRootName={setRootName}
        saveRoot={saveRoot}
        setShowRootDraft={setShowRootDraft}
        childDrafts={childDrafts}
        setChildDrafts={setChildDrafts}
        saveChild={saveChild}
        onRename={renameCategory}
        onAddChild={(parentId) => setChildDrafts((m) => ({ ...m, [parentId]: "" }))}
        onDelete={handleDelete}
        expandedRowKeys={expandedRowKeys}
        setExpandedRowKeys={setExpandedRowKeys}
      />

      {!showRootDraft && (
        <FloatButton
          icon={<PlusOutlined />}
          type="primary"
          shape="circle"
          tooltip="Додати групу"
          onClick={() => setShowRootDraft(true)}
          style={{ right: 16, bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)", boxShadow: "0 10px 24px rgba(0,0,0,.18)" }}
        />
      )}
    </>
  );
}
